import { useTranslation } from 'react-i18next';
import { useCampaignEditorContext } from '../state/hooks/useCampaignEditorContext';
import {
  CampaignEditorState,
  TargetAudienceType,
  PurchaseCheckUnit,
  TargetLookbackPeriod,
} from '../state/CampaignEditorState';

import { CampaignEditorTab } from './useActiveTab';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { useFeatureFlags } from 'core/app/AppContext';
import moment from 'moment-timezone';

export type CampaignValidationProps = Record<TargetAudienceType, string[]>;

export type CampaignValidation = Record<
  CampaignEditorTab,
  {
    isValid: boolean;
    details?: Array<{
      title: string;
      errors: string[];
      validProps: string[];
    }>;
  }
> & {
  isValidCampaign: boolean;
};

function validateAudience(campaign: CampaignEditorState['campaign'], t: TFunction, isFeatureFlagEnabled: boolean) {
  const { targetAudience, enablePurchaseCheck, purchaseCheckDays, purchaseCheck, additionalSegmentation } = campaign;

  return targetAudience.map(({ audience, days, dateType, startDate, endDate, isUsingDateRange }) => {
    const errors: string[] = [];
    const validProps: string[] = [];

    if (isUsingDateRange) {
      if (!startDate) {
        errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.startDate'));
      }

      if (!endDate) {
        errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.endDate'));
      }

      if (startDate && endDate) {
        const startDateStr = startDate.format('DD/MM/YYYY');
        const endDateStr = endDate.format('DD/MM/YYYY');
        validProps.push(
          t('bulkRetargetingCampaigns.editor.review.audience.validProps.between', { startDateStr, endDateStr }),
        );
      }
    } else {
      if (
        days &&
        (isNaN(days) ||
          days < 1 ||
          (dateType === TargetLookbackPeriod.Days && days > 180) ||
          (dateType === TargetLookbackPeriod.Months && days > 6))
      ) {
        errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.days'));
      } else if (days) {
        if (dateType === TargetLookbackPeriod.Months) {
          if (days === 1) {
            validProps.push(t('bulkRetargetingCampaigns.editor.review.audience.validProps.month', { days }));
          } else {
            validProps.push(t('bulkRetargetingCampaigns.editor.review.audience.validProps.months', { days }));
          }
        } else {
          if (days === 1) {
            validProps.push(t('bulkRetargetingCampaigns.editor.review.audience.validProps.day', { days }));
          } else {
            validProps.push(t('bulkRetargetingCampaigns.editor.review.audience.validProps.days', { days }));
          }
        }
      }
    }

    // Validate purchase check
    // When feature flag is enabled: check purchaseCheck object, otherwise check enablePurchaseCheck
    const isPurchaseCheckEnabled = isFeatureFlagEnabled
      ? purchaseCheck?.[audience]?.enablePurchaseCheck ?? false
      : enablePurchaseCheck[audience] ?? false;

    if (isPurchaseCheckEnabled) {
      if (isFeatureFlagEnabled) {
        // New validation logic when feature flag is enabled - use purchaseCheck object
        const purchaseCheckData = purchaseCheck?.[audience];
        if (!purchaseCheckData) {
          // Fallback to old structure if new structure doesn't exist
          const purchaseCheckDaysValue = purchaseCheckDays[audience] ?? 1;
          if (isNaN(purchaseCheckDaysValue) || purchaseCheckDaysValue < 1 || purchaseCheckDaysValue > 180) {
            errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.purchaseCheckDaysRange'));
          } else {
            if (purchaseCheckDaysValue === 1) {
              validProps.push(
                t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckDay', {
                  days: purchaseCheckDaysValue,
                }),
              );
            } else {
              validProps.push(
                t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckDays', {
                  days: purchaseCheckDaysValue,
                }),
              );
            }
          }
        } else {
          const {
            purchaseCheckDays: purchaseCheckDaysValue,
            purchaseCheckMonths,
            purchaseCheckUnit,
            purchaseCheckStartDate,
            purchaseCheckEndDate,
            isPurchaseCheckUsingDateRange,
          } = purchaseCheckData;

          if (isPurchaseCheckUsingDateRange) {
            // Validate date range mode
            if (!purchaseCheckStartDate) {
              errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.purchaseCheckStartDate'));
            }

            if (!purchaseCheckEndDate) {
              errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.purchaseCheckEndDate'));
            }

            if (purchaseCheckStartDate && purchaseCheckEndDate) {
              const startDateStr = purchaseCheckStartDate.format('DD/MM/YYYY');
              const endDateStr = purchaseCheckEndDate.format('DD/MM/YYYY');
              validProps.push(
                t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckDateRange', {
                  startDateStr,
                  endDateStr,
                }),
              );
            }
          } else {
            // Validate days/months mode
            if (purchaseCheckUnit === PurchaseCheckUnit.Months) {
              // Validate months (max is 6 per getMaxMonths utility)
              const monthsValue = purchaseCheckMonths ?? 1;
              if (isNaN(monthsValue) || monthsValue < 1 || monthsValue > 6) {
                errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.purchaseCheckDaysRange'));
              } else {
                if (monthsValue === 1) {
                  validProps.push(
                    t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckMonth', {
                      months: monthsValue,
                    }),
                  );
                } else {
                  validProps.push(
                    t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckMonths', {
                      months: monthsValue,
                    }),
                  );
                }
              }
            } else {
              // Validate days (default or when unit is 'days')
              const daysValue = purchaseCheckDaysValue ?? 1;
              if (isNaN(daysValue) || daysValue < 1 || daysValue > 180) {
                errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.purchaseCheckDaysRange'));
              } else {
                if (daysValue === 1) {
                  validProps.push(
                    t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckDay', {
                      days: daysValue,
                    }),
                  );
                } else {
                  validProps.push(
                    t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckDays', {
                      days: daysValue,
                    }),
                  );
                }
              }
            }
          }
        }
      } else {
        // Original validation logic when feature flag is disabled
        const purchaseCheckDaysValue = purchaseCheckDays[audience] ?? 1;

        // Validate purchase check days value
        if (isNaN(purchaseCheckDaysValue) || purchaseCheckDaysValue < 1 || purchaseCheckDaysValue > 180) {
          errors.push(t('bulkRetargetingCampaigns.editor.review.audience.errors.purchaseCheckDaysRange'));
        } else {
          validProps.push(
            t('bulkRetargetingCampaigns.editor.review.audience.validProps.purchaseCheckDays', {
              days: purchaseCheckDaysValue,
            }),
          );
        }
      }
    }

    if (additionalSegmentation[audience]) {
      validProps.push(t('bulkRetargetingCampaigns.editor.review.audience.validProps.additionalSegmentation'));
    }

    return {
      title: t(`bulkRetargetingCampaigns.editor.review.audienceType.${audience}`),
      errors,
      validProps,
    };
  });
}

function validateContent(campaign: CampaignEditorState['campaign'], t: TFunction) {
  const { targetAudience, template, subjectLine } = campaign;

  return targetAudience.map(({ audience, days }) => {
    const errors: string[] = [];
    const validProps: string[] = [];

    if (!template[audience]) {
      errors.push(t('bulkRetargetingCampaigns.editor.review.content.errors.template'));
    } else {
      validProps.push(
        t('bulkRetargetingCampaigns.editor.review.content.validProps.template', {
          template: template[audience]?.templateName ?? 'N/A',
        }),
      );
    }

    if (!subjectLine[audience]) {
      errors.push(t('bulkRetargetingCampaigns.editor.review.content.errors.subjectLine'));
    } else {
      validProps.push(
        t('bulkRetargetingCampaigns.editor.review.content.validProps.subjectLine', {
          subjectLine: subjectLine[audience].label,
        }),
      );
    }

    return {
      title: t(`bulkRetargetingCampaigns.editor.review.audienceType.${audience}`),
      errors,
      validProps,
    };
  });
}

function validateScheduling(campaign: CampaignEditorState['campaign'], t: TFunction) {
  const errors: string[] = [];
  const validProps: string[] = [];

  const { sendDate, minSendDate, maxSendDate } = campaign;

  if (sendDate && maxSendDate) {
    const sendDateMoment = moment(sendDate);
    // Check if sendDate is on or before maxSendDate
    if (sendDateMoment.isAfter(maxSendDate, 'day')) {
      errors.push(t('bulkRetargetingCampaigns.editor.review.scheduling.scheduledDateAfterSixMonthsMessage'));
    }
  }

  if (sendDate && minSendDate) {
    const sendDateMoment = moment(sendDate);
    // Check if sendDate is on or after minSendDate
    if (sendDateMoment.isBefore(minSendDate, 'day')) {
      errors.push(t('bulkRetargetingCampaigns.editor.review.scheduling.scheduledDateBeforeEndDate'));
    }
  }

  return {
    title: undefined,
    errors,
    validProps,
  };
}

export const useCampaignValidator = (): CampaignValidation => {
  const { state } = useCampaignEditorContext();
  const { t } = useTranslation('campaigns');
  const { flagEnabled } = useFeatureFlags();
  const isFeatureFlagEnabled = flagEnabled('ld-bulk-date-pickers');

  const audienceValidationDetails = useMemo(() => validateAudience(state.campaign, t, isFeatureFlagEnabled), [
    state.campaign,
    t,
    isFeatureFlagEnabled,
  ]);
  const contentValidationDetails = useMemo(() => validateContent(state.campaign, t), [state.campaign, t]);
  const schedulingValidationDetails = useMemo(() => validateScheduling(state.campaign, t), [state.campaign, t]);

  const audienceValidation = {
    isValid: audienceValidationDetails.every(({ errors }) => errors.length === 0),
    details: audienceValidationDetails,
  };

  const contentValidation = {
    isValid: contentValidationDetails.every(({ errors }) => errors.length === 0),
    details: contentValidationDetails,
  };

  const schedulingValidation = {
    isValid: schedulingValidationDetails.errors.length === 0,
    details: schedulingValidationDetails.errors.length > 0 ? [schedulingValidationDetails] : undefined,
  };

  return {
    isValidCampaign: audienceValidation.isValid && contentValidation.isValid && schedulingValidation.isValid,
    [CampaignEditorTab.Audience]: audienceValidation,
    [CampaignEditorTab.Content]: contentValidation,
    [CampaignEditorTab.Scheduling]: schedulingValidation,
  };
};
